import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useAssetsApi } from 'src/modules/assetLibrary/api/AssetsApi';
import { FileLinkWithMimeType } from 'src/constants/types';
import { reportErrorToUser } from 'src/api/axiosClient/errorHandling';
import { GENERIC_ERROR_MESSAGE } from 'src/constants/messages';
import { ensureFileExtension, getFileNameFromUrl, triggerBlobDownload } from 'src/helpers/files';

interface DownloadFileIconProps {
  attachments: FileLinkWithMimeType[];
  isVisible: boolean;
  isMobile: boolean;
}

const DownloadFileIcon: React.FC<DownloadFileIconProps> = ({
  attachments,
  isVisible,
  isMobile,
}) => {
  const { getFileContent } = useAssetsApi();

  const downloadFile = async (fileUrl: string, filename: string = 'file') => {
    try {
      const response = await getFileContent(fileUrl);
      triggerBlobDownload(new Blob([response.data]), filename);
    } catch (error) {
      reportErrorToUser(error, GENERIC_ERROR_MESSAGE);
    }
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (attachments && attachments.length > 0) {
      // Download the first attachment (or could implement multi-attachment selection)
      const firstAttachment = attachments[0];
      const filename = ensureFileExtension(
        firstAttachment.name || getFileNameFromUrl(firstAttachment.url) || 'attachment',
        firstAttachment.mimeType,
      );

      downloadFile(firstAttachment.url, filename);
    }
  };

  if (!attachments || attachments.length === 0 || isMobile) {
    return null;
  }

  return (
    <IconButton
      onClick={handleDownload}
      color="primary"
      size="small"
      sx={{
        position: 'absolute',
        top: 8,
        right: 8,
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 0.2s',
        backgroundColor: 'background.paper',
        borderRadius: 1,
        padding: '2px',
        zIndex: 10,
        '&:hover': {
          backgroundColor: 'background.paper',
        },
      }}
    >
      <Tooltip title="Download">
        <DownloadIcon fontSize="small" />
      </Tooltip>
    </IconButton>
  );
};

export default DownloadFileIcon;
